/**
 * React Query hooks for libraries
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLibraries,
  getLibrary,
  createLibrary,
  updateLibrary,
  deleteLibrary,
  getLibraryStats,
} from '@/api/libraries';
import {
  Library,
  LibraryStats,
  CreateLibraryRequest,
  UpdateLibraryRequest,
} from '@/types/api';
import { getErrorMessage } from '@/api/client';

/**
 * Hook to fetch all libraries
 */
export const useLibraries = () => {
  return useQuery<Library[], Error>({
    queryKey: ['libraries'],
    queryFn: getLibraries,
  });
};

/**
 * Hook to fetch single library
 */
export const useLibrary = (id: number) => {
  return useQuery<Library, Error>({
    queryKey: ['libraries', id],
    queryFn: () => getLibrary(id),
    enabled: !!id,
  });
};

/**
 * Hook to fetch library statistics
 */
export const useLibraryStats = (id: number) => {
  return useQuery<LibraryStats, Error>({
    queryKey: ['libraries', id, 'stats'],
    queryFn: () => getLibraryStats(id),
    enabled: !!id,
  });
};

/**
 * Hook to create library
 */
export const useCreateLibrary = () => {
  const queryClient = useQueryClient();

  return useMutation<Library, Error, CreateLibraryRequest>({
    mutationFn: createLibrary,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['libraries'] });
    },
    onError: (error) => {
      console.error('Failed to create library:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to update library
 */
export const useUpdateLibrary = () => {
  const queryClient = useQueryClient();

  return useMutation<Library, Error, { id: number; data: UpdateLibraryRequest }>({
    mutationFn: ({ id, data }) => updateLibrary(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['libraries'] });
      queryClient.invalidateQueries({ queryKey: ['libraries', data.id] });
    },
    onError: (error) => {
      console.error('Failed to update library:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to delete library
 */
export const useDeleteLibrary = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: deleteLibrary,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['libraries'] });
    },
    onError: (error) => {
      console.error('Failed to delete library:', getErrorMessage(error));
    },
  });
};
